<template>
    <div
        class="px-8 pt-6 pb-8 rounded-lg border bg-slate-50 border-slate-200 relative"
    >
        <Transition name="fade" mode="out-in">
            <div
                v-if="
                    userTrip &&
                    currentTrip &&
                    timerExpired &&
                    remainingTrips > 0 &&
                    !showingAprilFoolsReveal
                "
            >
                <div
                    class="backdrop-blur-sm bg-white/50 p-8 absolute inset-0 z-20 flex items-center justify-center"
                >
                    <div
                        class="rounded-lg max-w-lg flex bg-white p-8 px-12 border border-slate-200 flex-col shadow items-center text-center"
                    >
                        <div
                            class="bg-slate-200 w-14 h-14 flex items-center justify-center mb-6 rounded-full"
                        >
                            <Icon icon="hourglass" size="large" />
                        </div>
                        <h4
                            class="text-xl lg:text-2xl font-medium text-slate-800 mb-5"
                        >
                            Swap opportunity expired
                        </h4>
                        <p class="text-slate-500 text-base lg:text-lg mb-6">
                            Sorry, this offer expired. Try to found another swap
                            option.
                        </p>
                        <button
                            type="button"
                            @click="generateNewTrip"
                            class="flex items-center font-medium gap-1 bg-orange-600 hover:bg-orange-400 transition-colors text-white px-4 pr-5 py-3 rounded"
                        >
                            <Icon icon="refresh" />
                            Find another swap
                        </button>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- Header is always displayed -->
        <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-4">
            <h2 class="text-xl lg:text-2xl font-medium text-slate-800">
                Swap option
            </h2>
            <div v-if="userTrip && !isCheckingForTrips && !isGeneratingNewTrip && !noTripsAvailable" class="flex gap-4 justify-between lg:items-center">
                <button
                    type="button"
                    class="flex items-center gap-2 px-4 py-2 font-medium text-slate-700 hover:text-slate-400 transition-colors"
                    @click="generateNewTrip"
                    :disabled="isGeneratingNewTrip"
                >
                    <Icon icon="refresh" class="w-5 h-5 text-slate-500" />
                    Find another
                </button>
                <button
                    class="flex items-center font-medium gap-1 bg-orange hover:bg-orange-400 transition-colors text-white px-3 pr-4 py-1 rounded"
                    @click="acceptTrip"
                    :disabled="isGeneratingNewTrip"
                >
                    <Icon
                        icon="check"
                        :width="36"
                        :height="36"
                        class="text-white"
                    />
                    Swap
                </button>
            </div>
        </div>

        <div
            class="h-0.5 mb-7 self-stretch [background:linear-gradient(90deg,rgba(226,232,240,0.50)_0%,#E2E8F0_4.94%,#E2E8F0_94.46%,rgba(226,232,240,0.50)_100%)]"
        ></div>

        <!-- Show empty state when no user trip is available -->
        <template v-if="!userTrip">
            <div class="p-6">
                <EmptyState
                    icon="swap"
                    title="No Trip Swap Opportunities"
                    description="Trip Swap opportunities will be available once you've added trips to your Redemption Tracker. Please add a trip first before attempting to swap."
                />
            </div>
        </template>
        
        <template v-else-if="isCheckingForTrips">
            <!-- Loading State when checking for trips -->
            <div class="h-full flex flex-col items-center justify-center py-10">
                <div class="relative w-24 h-24 mb-8">
                    <LoadingState :loading="true" size="large" />
                </div>
                <h3 class="text-xl font-semibold text-slate-800 mb-2">
                    Checking for Available Trips
                </h3>
                <p class="text-slate-500 text-center px-8">
                    Please wait while we search for exciting new trip options
                    for you...
                </p>
            </div>
        </template>

        <template v-else-if="isGeneratingNewTrip">
            <!-- Loading State when generating a new trip -->
            <div class="h-full flex flex-col items-center justify-center py-10">
                <div class="relative w-24 h-24 mb-8">
                    <LoadingState :loading="true" size="large" />
                </div>
                <h3 class="text-xl font-semibold text-slate-800 mb-2">
                    Finding a New Trip Swap
                </h3>
                <p class="text-slate-500 text-center px-8">
                    Please wait while we search for another exciting trip option
                    for you...
                </p>
            </div>
        </template>

        <template v-else-if="noTripsAvailable">
            <!-- Empty State when no trips are available -->
            <div class="h-full relative">
                <!-- Failure Notification -->
                <div
                    v-if="showFailureNotification"
                    class="absolute top-4 left-0 right-0 mx-auto w-4/5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-md z-10 transition-all duration-300 ease-in-out transform"
                    style="max-width: 90%"
                >
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <svg
                                class="h-5 w-5 text-red-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm font-medium">
                                No new trip matches found. Please try again
                                later.
                            </p>
                        </div>
                    </div>
                </div>

                <EmptyState
                    icon="alert"
                    title="No Available Trip Swaps"
                    description="We couldn't find any available trips to swap to at this time. Please try again later when more options become available."
                    action-label="Check for Available Trip Swaps"
                    container-styles="py-10 h-full flex items-center text-center justify-center"
                    @action="checkForAvailableSwaps"
                />
            </div>
        </template>

        <template v-else>
            <!-- Trip Swap Option when available -->
            <div class="relative">
                <img
                    :src="currentTrip?.imageUrl"
                    alt="Trip Image"
                    class="w-full h-64 object-cover rounded-lg"
                />
                <div
                    class="absolute top-0 right-0 m-4 bg-orange text-white px-3 py-1 rounded-lg font-semibold transition-all duration-200"
                    :class="{
                        'animate-pulse-fast': countdown <= 10,
                    }"
                >
                    {{ countdown }}s
                </div>
            </div>

            <div class="py-6">
                <!-- Success Notification below image -->

                <div
                    v-if="showSuccessNotification"
                    class="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-sm transition-all duration-300 ease-in-out transform"
                >
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <svg
                                class="h-5 w-5 text-green-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm font-medium">
                                Success! We found an exciting new trip option
                                for you.
                            </p>
                        </div>
                    </div>
                </div>

                <TripHeader
                    class="mb-6"
                    :trip="currentTrip"
                    :user-trip="userTrip"
                />
                <Redemptions
                    :user-trip="userTrip"
                    :redemptions="currentTrip.redemptions"
                />
            </div>
        </template>
    </div>
</template>
<script setup>
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import TripHeader from '@/components/trip-swap/trip-header.vue';
import Redemptions from '@/components/trip-swap/redemptions.vue';
import Icon from '@/components/ui/icon/icon.vue';

const {
    currentTrip,
    userTrip,
    isCheckingForTrips,
    isGeneratingNewTrip,
    noTripsAvailable,
    countdown,
    showSuccessNotification,
    showFailureNotification,
    timerExpired,
    remainingTrips,
    showingAprilFoolsReveal,
} = defineProps([
    'currentTrip',
    'userTrip',
    'isCheckingForTrips',
    'isGeneratingNewTrip',
    'noTripsAvailable',
    'countdown',
    'showSuccessNotification',
    'showFailureNotification',
    'timerExpired',
    'remainingTrips',
    'showingAprilFoolsReveal',
]);

const emit = defineEmits([
    'generateNewTrip',
    'acceptTrip',
    'checkForAvailableSwaps',
]);

const generateNewTrip = () => {
    emit('generateNewTrip');
};

const acceptTrip = () => {
    emit('acceptTrip');
};

const checkForAvailableSwaps = () => {
    emit('checkForAvailableSwaps');
};
</script>
